import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { BoxProgramColorSelection } from "@/modules/boxProgrammation/components/boxProgramColorSelection"
import { CoachCategoryFormStepper } from "@modules/coachCategories/components/coachCategoryFormStepper"
import { CoachCategoryOfferInput } from "@modules/coachCategories/components/coachCategoryOfferInput"
import {
  getCoachCategoryInfoSchema,
  type CoachCategoryInfoFormFields,
} from "@modules/coachCategories/formTypes/coachCategoryFormTypes"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"

export const CoachCategoryInfoForm = withForm({
  defaultValues: {} as CoachCategoryInfoFormFields,
  props: {} as {
    isEditing: boolean
    onValidate: () => void
  } & React.ComponentProps<"form">,
  render: function CoachCategoryInfoFormInner({ form, isEditing, onValidate }) {
    const { hideSidebar } = useSidebar()
    const schema = getCoachCategoryInfoSchema()

    return (
      <form
        className="flex w-full flex-col justify-center px-5 pt-18 pb-6"
        onSubmit={(e) => {
          e.preventDefault()
          onValidate()
        }}
      >
        <CoachCategoryFormStepper step={1} />

        <Text fontStyle="TL" className="mt-4 mb-2">
          {isEditing
            ? word("coach.planning.category.form.update.title")
            : word("coach.planning.category.form.add.title")}
        </Text>

        <Text fontStyle="BS" className="mb-10">
          {word("coach.planning.category.form.add.description")}
        </Text>

        <div className="flex flex-col gap-8">
          <form.AppField name="name" validators={{ onChange: schema.shape.name, onBlur: schema.shape.name }}>
            {(field) => (
              <field.Field>
                <field.Label required>{word("coach.planning.category.form.name.label")}</field.Label>
                <field.Content>
                  <field.Input placeholder={word("coach.planning.category.form.name.placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField
            name="description"
            validators={{ onChange: schema.shape.description, onBlur: schema.shape.description }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.programmation.form.description.label")}</field.Label>
                <field.Content>
                  <field.Textarea
                    placeholder={word("box.programmation.form.description.placeholder")}
                    className="field-sizing-fixed"
                    rows={5}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="color">
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.programmation.form.label.color")}</field.Label>
                <field.Content>
                  <BoxProgramColorSelection
                    selectedColor={field.state.value}
                    onChangeSelectedColor={(color) => {
                      field.handleChange(color)
                    }}
                    striped
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <CoachCategoryOfferInput form={form} />

          <form.AppField name="visibleToClients">
            {(field) => (
              <field.Field>
                <field.Label>{word("coach.planning.category.form.visibility.label")}</field.Label>
                <field.Content>
                  <field.SwitchInline textSwitch={word("coach.planning.category.form.visibility.textSwitch")} />
                  <Text fontStyle="LM" className="px-4 text-textOnSurface-default">
                    {word("coach.planning.category.form.visibility.description")}
                  </Text>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <div className="mt-8 mb-12 flex w-full grow flex-col gap-4 sm:flex-row">
          <Button
            variant="outline"
            size="lg"
            className="grow"
            onClick={() => {
              hideSidebar()
            }}
          >
            {word("global.cancel")}
          </Button>
          <Button variant="primary" size="lg" className="grow" type="submit">
            {word("global.next")}
          </Button>
        </div>
      </form>
    )
  },
})
